import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import LoginPage from './pages/LoginPage';
import Overview from './pages/Overview';
import ManageOffices from './pages/ManageOffices';
import ManageUsers from './pages/ManageUsers';
import SystemConfig from './pages/SystemConfig';
import Employees from './pages/Employees';
import Recruitment from './pages/Recruitment';
import Onboarding from './pages/Onboarding';
import Attendance from './pages/Attendance';
import Performance from './pages/Performance';
import Payroll from './pages/Payroll';
import MySalary from './pages/MySalary';
import LeaveManagement from './pages/LeaveManagement';
import ContractLedger from './pages/ContractLedger';
import LettersLedger from './pages/LettersLedger';
import Tasks from './pages/Tasks';
import Schedule from './pages/Schedule';
import Team from './pages/Team';
import OfficeExpenseManagement from './pages/OfficeExpenseManagement';
import PlatformAnalytics from './pages/PlatformAnalytics';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import MyLeave from './pages/MyLeave';
import MyTasks from './pages/employee/MyTasks';
import OfficeConfiguration from './pages/OfficeConfiguration';
import MyCalendar from './pages/employee/MyCalendar';
import MyProfile from './pages/employee/MyProfile';
import MyExpenses from './pages/employee/MyExpenses';
import ExpenseManagement from './pages/ExpenseManagement';
import PolicyManagement from './pages/PolicyManagement';
import CompanyPolicies from './pages/CompanyPolicies';
import TeamCalendar from './pages/TeamCalendar';
import NewsManagement from './pages/NewsManagement';
import Newsroom from './pages/Newsroom';
import AdminDataManagement from './pages/AdminDataManagement';
import AuditTrail from './pages/AuditTrail';
import Ping from './pages/Ping';
import VerifyEmployee from './pages/VerifyEmployee';
import EmailConfiguration from './pages/EmailConfiguration';
import LetterheadSettings from './pages/LetterheadSettings';
import QRScanLogs from './pages/QRScanLogs';
import ExpenseCategories from './pages/ExpenseCategories';
import ExpenseSettings from './pages/ExpenseSettings';
import Leads from './pages/Leads';
import Timesheet from './pages/Timesheet';
import TeamTimesheets from './pages/TeamTimesheets';
import TimesheetCategories from './pages/TimesheetCategories';
import MeetingPad from './pages/MeetingPad';
import Documentation from './pages/Documentation';
import ModuleManagement from './pages/ModuleManagement';
import BillingManagement from './pages/BillingManagement';
import OfficeBilling from './pages/OfficeBilling';
import CheckInModal from './components/CheckInModal';
import DayPlannerModal from './components/DayPlannerModal';
import SubscriptionExpired from './components/SubscriptionExpired';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

// Main App Layout Component
const AppLayout = () => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [dayPlanned, setDayPlanned] = useState(false);
  
  const { user, currentOffice, isEmployee, isSuperAdmin } = useAuth();
  const { billingBlocked } = useAuth();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Super admins bypass check-in
  const needsCheckIn = !isSuperAdmin() && !checkedIn;
  const showDayPlanner = !isSuperAdmin() && checkedIn && !dayPlanned;

  // Block office users whose billing grace period has lapsed
  if (billingBlocked && !isSuperAdmin()) {
    return <SubscriptionExpired />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-layout">
      {needsCheckIn && (
        <CheckInModal onCheckInComplete={() => setCheckedIn(true)} />
      )}
      {showDayPlanner && (
        <DayPlannerModal onComplete={() => setDayPlanned(true)} />
      )}
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar 
          theme={theme} 
          onThemeToggle={toggleTheme}
          currentUser={user}
          currentOffice={currentOffice}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-[1440px] mx-auto">
            <Routes>
              <Route path="/" element={<Navigate to="/overview" replace />} />
              <Route path="/overview" element={<Overview />} />
              <Route path="/manage-offices" element={<ManageOffices />} />
              <Route path="/manage-users" element={<ManageUsers />} />
              <Route path="/system-config" element={<SystemConfig />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/recruitment" element={<Recruitment />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/payroll" element={<Payroll />} />
              <Route path="/my-salary" element={<MySalary />} />
              <Route path="/leave-management" element={<LeaveManagement />} />
              <Route path="/contracts" element={<ContractLedger />} />
              <Route path="/letters" element={<LettersLedger />} />
              <Route path="/profile" element={isEmployee() ? <MyProfile /> : <Profile />} />
              <Route path="/my-leave" element={<MyLeave />} />
              <Route path="/my-tasks" element={<MyTasks />} />
              <Route path="/my-calendar" element={<MyCalendar />} />
              <Route path="/my-expenses" element={<MyExpenses />} />
              <Route path="/expense-management" element={<ExpenseManagement />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/team" element={<Team />} />

              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/policy-management" element={<PolicyManagement />} />
              <Route path="/company-policies" element={<CompanyPolicies />} />
              <Route path="/team-calendar" element={<TeamCalendar />} />
              <Route path="/newsroom" element={<Newsroom />} />
              <Route path="/news-management" element={<NewsManagement />} />
              <Route path="/admin/data-management" element={<AdminDataManagement />} />
              <Route path="/platform-analytics" element={<PlatformAnalytics />} />
              <Route path="/admin/email-config" element={<EmailConfiguration />} />
              <Route path="/admin/qr-scan-logs" element={<QRScanLogs />} />
              <Route path="/admin/expense-categories" element={<ExpenseCategories />} />
              <Route path="/admin/expense-settings" element={<ExpenseSettings />} />
              {/* Office Admin routes */}
              <Route path="/office/configuration" element={<OfficeConfiguration />} />
              <Route path="/office/email-settings" element={<EmailConfiguration officeLevel={true} />} />
              <Route path="/office/letterhead" element={<LetterheadSettings />} />
              <Route path="/office/qr-scan-logs" element={<QRScanLogs officeLevel={true} />} />
              <Route path="/office/expense-categories" element={<ExpenseCategories officeLevel={true} />} />
              <Route path="/office/expense-settings" element={<OfficeExpenseManagement />} />
              <Route path="/audit-trail" element={<AuditTrail />} />
              <Route path="/ping" element={<Ping />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/timesheet" element={<Timesheet />} />
              <Route path="/team-timesheets" element={<TeamTimesheets />} />
              <Route path="/office/timesheet-categories" element={<TimesheetCategories />} />
              <Route path="/meeting-pad" element={<MeetingPad />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/module-management" element={<ModuleManagement />} />
              <Route path="/billing" element={<BillingManagement />} />
              <Route path="/office/billing" element={<OfficeBilling />} />
              <Route path="*" element={<Navigate to="/overview" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify" element={<VerifyEmployee />} />
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            } 
          />
        </Routes>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
